import { useState } from 'react';
import { format } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';
import GiftCardDetailsModal from '../../giftCards/GiftCardDetailsModal';
import useGiftCardActions from '../../../hooks/giftCards/useGiftCardActions';
import { CustomerOverview } from '../../../types/customers';
import { formatDollarAmount } from '../../../utils/global/helper';
import { BORDER, HAIRLINE } from './colors';

type GiftCard = CustomerOverview['giftCards'][number];

interface Props {
  customer: CustomerOverview;
}

function BalanceLine({ label, amount }: { label: string; amount: number }) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      gap="12px">
      <Typography variant="h4" sx={{ color: 'text.primary' }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: 'text.primary' }}>
        {formatDollarAmount(amount / 100)}
      </Typography>
    </Stack>
  );
}

function GiftCardRow({
  giftCard,
  onSelect,
}: {
  giftCard: GiftCard;
  onSelect: (giftCardId: string) => void;
}) {
  const purchased = giftCard.purchasedAt
    ? `Purchased ${format(new Date(giftCard.purchasedAt), 'MMM d, yyyy')}`
    : null;

  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap="12px"
      onClick={() => onSelect(giftCard.id)}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px',
        border: `1px solid ${HAIRLINE}`,
        borderRadius: '10px',
        backgroundColor: '#fff',
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { borderColor: BORDER },
      }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }}>
          {formatDollarAmount(giftCard.currentBalance / 100)} remaining
        </Typography>
        {purchased && (
          <Typography
            variant="caption"
            component="p"
            sx={{ color: 'text.tertiary', marginTop: '3px' }}>
            {purchased}
          </Typography>
        )}
      </Box>
      <Typography variant="h5" sx={{ color: 'text.secondary', flexShrink: 0 }}>
        View
      </Typography>
    </Stack>
  );
}

function CustomerBalancesSection({ customer }: Props) {
  const [detailsGiftCardId, setDetailsGiftCardId] = useState<string | null>(
    null,
  );
  const { getOptions } = useGiftCardActions();

  const giftCards = (customer.giftCards ?? []).filter(
    giftCard => giftCard.currentBalance > 0,
  );
  const unusedDeposits = customer.summary?.unusedDepositsTotal ?? 0;
  if (!giftCards.length && unusedDeposits <= 0) return null;

  const totalBalance = giftCards.reduce(
    (acc, giftCard) => acc + giftCard.currentBalance,
    0,
  );
  const selectedGiftCard = giftCards.find(
    giftCard => giftCard.id === detailsGiftCardId,
  );

  return (
    <Stack
      gap="14px"
      sx={{ mt: '32px', pt: '24px', borderTop: `1px solid ${HAIRLINE}` }}>
      {giftCards.length > 0 && (
        <>
          <BalanceLine label="Gift card balance" amount={totalBalance} />
          <Stack gap="10px">
            {giftCards.map(giftCard => (
              <GiftCardRow
                key={giftCard.id}
                giftCard={giftCard}
                onSelect={setDetailsGiftCardId}
              />
            ))}
          </Stack>
        </>
      )}
      {unusedDeposits > 0 && (
        <BalanceLine label="Unused deposits" amount={unusedDeposits} />
      )}
      <GiftCardDetailsModal
        open={!!detailsGiftCardId}
        onClose={() => setDetailsGiftCardId(null)}
        giftCardId={detailsGiftCardId}
        options={
          selectedGiftCard
            ? getOptions({
                id: selectedGiftCard.id,
                currentBalance: selectedGiftCard.currentBalance,
                customerId: customer.customerId,
                customer: { user: customer.user },
              })
            : undefined
        }
      />
    </Stack>
  );
}

export default CustomerBalancesSection;
